<template>
  <div>
    <!-- Breadcrumb + the one share button -->
    <div class="card theme-card px-3 py-3 mb-3">
      <div class="d-flex flex-wrap align-items-center justify-content-between">
        <div class="mb-2 mb-md-0 desktop-only">
          <button
            class="btn btn-link p-0 breadcrumb-link"
            @click="$emit('back', null)"
          >
            Documents
          </button>
          <span v-for="(crumb, i) in trail" :key="crumb.id">
            <span class="mx-2 text-muted-theme">/</span>
            <button
              v-if="i < trail.length - 1"
              class="btn btn-link p-0 breadcrumb-link"
              @click="$emit('back', crumb)"
            >
              <truncated-text :text="crumb.name" max="26ch" />
            </button>
            <strong v-else
              ><truncated-text :text="crumb.name" max="26ch"
            /></strong>
          </span>
          <span class="text-muted-theme ml-2 small">{{ summary }}</span>
        </div>

        <!-- Narrow screens get one step back instead of the path: the whole trail wraps onto
             three lines by the third folder down. -->
        <div class="mb-2 trail-compact">
          <button
            class="btn btn-link p-0 breadcrumb-link"
            @click="$emit('back', parentCrumb)"
          >
            <i class="fas fa-chevron-left fa-xs mr-1"></i>
            <truncated-text
              :text="parentCrumb ? parentCrumb.name : 'Documents'"
              max="24ch"
            />
          </button>
          <div v-if="currentCrumb" class="trail-current">
            <strong
              ><truncated-text :text="currentCrumb.name" max="30ch"
            /></strong>
            <span class="text-muted-theme ml-2 small">{{ summary }}</span>
          </div>
        </div>

        <div class="d-flex align-items-center contents-actions">
          <div class="input-group mr-3 folder-search">
            <div class="input-group-prepend">
              <span class="input-group-text"
                ><i class="fas fa-search"></i
              ></span>
            </div>
            <input
              type="text"
              class="form-control"
              placeholder="Search in this folder"
              v-model="searchTerm"
              @input="onSearchInput"
            />
            <button
              v-if="searchTerm"
              type="button"
              class="clear-search"
              title="Clear search"
              @click="clearSearch"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>

          <!-- With nesting, the lists most likely to hold empty folders are the ones inside a
               folder — the one place the filter was not offered. -->
          <div
            v-if="emptyFolderCount"
            class="custom-control custom-checkbox hide-empty-control mr-3"
            v-tooltip.top="
              contentsQuery.hideEmpty
                ? 'Showing only folders with documents'
                : 'Hide the folders holding nothing'
            "
          >
            <input
              type="checkbox"
              class="custom-control-input"
              id="contentsHideEmpty"
              :checked="contentsQuery.hideEmpty"
              @change="toggleHideEmpty"
            />
            <label class="custom-control-label" for="contentsHideEmpty">
              Hide empty ({{ emptyFolderCount }})
            </label>
          </div>

          <button
            v-if="canCreate"
            class="btn btn-sm btn-theme text-nowrap mr-2"
            @click="newSubfolder"
          >
            New folder
          </button>

          <!-- Label follows the selection: "Share folder" with nothing ticked, "Share 2 documents" with some. -->
          <button
            v-if="perms.share"
            class="btn btn-sm btn-theme text-nowrap"
            @click="emitShare"
          >
            {{ shareLabel }}
          </button>
        </div>
      </div>
    </div>

    <dropzone
      v-if="perms.upload"
      :options="dropzoneOptions"
      :upload-handler="handleS3Upload"
      @success="uploadSuccess"
    />

    <div class="card theme-card overflow-auto">
      <alert v-if="isLoadingContents" class="m-4" />
      <alert v-if="!isLoadingContents && !contents.data.length" class="m-4"
        >This folder is empty</alert
      >

      <div
        v-if="!isLoadingContents && contents.data.length"
        class="table-responsive"
      >
        <node-table
          :rows="contents.data"
          :query="contentsQuery"
          :subject-user-id="subjectUserId"
          :selected="selectedIds"
          :can-share-documents="perms.share"
          @update:selected="selectedIds = $event"
          @share-document="shareOne"
          selectable
          @sort="sortBy"
          @changed="reload"
          @open-folder="$emit('open-folder', $event)"
          @share-folder="$emit('share-folder', $event)"
          @move-folder="$emit('move-folder', $event)"
          @show-audience="$emit('show-audience', $event)"
        />
      </div>

      <div class="p-3" v-if="contents.meta.last_page > 1">
        <pagination
          :current-page="contents.meta.current_page"
          :total-pages="contents.meta.last_page"
          :total="contents.meta.total"
          @page-changed="goToPage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { assertUploadableDicom } from "@/utils/dicom";
import { MAX_DOCUMENT_UPLOAD_MB } from "@/utils/fileHelpers.js";
import axios from "axios";
import { debounce } from "lodash";
import { mapActions, mapState } from "vuex";
import {
  SEARCH_DEBOUNCE_MS,
  folderPermissions,
  promptNewFolder,
  shareButtonLabel,
  toggleSort,
} from "./useDocumentsArea";

export default {
  name: "FolderContents",
  props: {
    folder: { type: Object, required: true },
    /** Every folder from the top down to this one, for the breadcrumb. */
    trail: { type: Array, default: () => [] },
    /** Whether this person may create folders here — decided by DocumentsArea, per subject. */
    canCreate: { type: Boolean, default: false },
    subjectUserId: { type: Number, default: null },
  },
  emits: [
    "back",
    "share",
    "open-folder",
    "open-failed",
    "share-folder",
    "move-folder",
    "show-audience",
  ],
  data() {
    return {
      searchTerm: "",
      selectedIds: [],
    };
  },
  computed: {
    ...mapState({
      contents: (state) => state.folders.contents,
      contentsQuery: (state) => state.folders.contentsQuery,
      isLoadingContents: (state) => state.folders.isLoadingContents,
    }),

    perms() {
      return folderPermissions(this, this.folder, this.subjectUserId);
    },

    /** Deliberately the UNFILTERED count, or it collapses to zero the moment it is applied. */
    emptyFolderCount() {
      return (this.contents.meta || {}).empty_folders || 0;
    },

    /** One level up, or null at the top — where the compact trail's back link goes. */
    parentCrumb() {
      return this.trail.length > 1 ? this.trail[this.trail.length - 2] : null;
    },

    /** The folder actually open. */
    currentCrumb() {
      return this.trail.length ? this.trail[this.trail.length - 1] : null;
    },

    /** "2 folders · 5 documents", counting only what this folder actually holds. */
    summary() {
      const meta = this.contents.meta || {};
      const documents = meta.total_documents || 0;
      const folders = (meta.total || 0) - documents;
      const plural = (n, word) => `${n} ${word}${1 === n ? "" : "s"}`;

      return `· ${plural(folders, "folder")} · ${plural(
        documents,
        "document"
      )}`;
    },

    shareLabel() {
      return shareButtonLabel(this.selectedIds.length);
    },

    dropzoneOptions() {
      return {
        url: `documents/folders/${this.folder.id}/add?owner_id=${this.subjectUserId}`,
        maxFilesize: MAX_DOCUMENT_UPLOAD_MB,
      };
    },
  },
  created() {
    this.runSearch = debounce(() => {
      this.patch({ search: this.searchTerm, page: 1 });
      this.reload();
    }, SEARCH_DEBOUNCE_MS);
  },
  mounted() {
    this.patch({ search: "", page: 1 });
    this.reload();
  },
  beforeUnmount() {
    // Otherwise a pending search fires after the component is gone and writes into the shared query.
    this.runSearch.cancel();
  },
  methods: {
    ...mapActions({ getContents: "folders/getContents" }),

    patch(patchValue) {
      this.$store.commit("folders/PATCH_QUERY", {
        key: "contentsQuery",
        patch: patchValue,
      });
    },

    reload() {
      // Drop selections: an id no longer on screen must not stay in a pending share.
      this.selectedIds = [];

      return this.getContents(this.folder.id).catch(() =>
        this.$emit("open-failed")
      );
    },

    onSearchInput() {
      this.runSearch();
    },

    sortBy(column) {
      this.patch({ ...toggleSort(this.contentsQuery, column), page: 1 });
      this.reload();
    },

    goToPage(page) {
      this.patch({ page });
      this.reload();
    },

    newSubfolder() {
      promptNewFolder(this, {
        subjectUserId: this.subjectUserId,
        parentId: this.folder.id,
        parent: this.folder,
        onCreated: () => this.reload(),
      });
    },

    /** One document, straight into the dialog the toolbar's bulk share already opens. */
    clearSearch() {
      this.runSearch.cancel();
      this.searchTerm = "";
      this.patch({ search: "", page: 1 });
      this.reload();
    },

    toggleHideEmpty() {
      this.patch({ hideEmpty: !this.contentsQuery.hideEmpty, page: 1 });
      this.reload();
    },

    shareOne(document) {
      this.$emit("share", { folder: this.folder, documentIds: [document.id] });
    },

    emitShare() {
      this.$emit("share", {
        folder: this.folder,
        documentIds: [...this.selectedIds],
      });
    },

    uploadSuccess() {
      this.reload();
    },

    /** The presigned-S3 upload flow. A damaged .dcm is refused before anything is stored. */
    async handleS3Upload(file, onProgress) {
      await assertUploadableDicom(file);

      const { data } = await this.$http.post("documents/upload-url", {
        original_file_name: file.name,
        owner_id: this.subjectUserId,
        size: file.size,
      });

      await axios.put(data.url, file, {
        headers: data.headers,
        onUploadProgress: (e) =>
          onProgress(Math.round((e.loaded * 100) / e.total)),
      });

      return await this.$http.post(`documents/folders/${this.folder.id}/add`, {
        temp_path: data.temp_path,
        original_file_name: file.name,
        size: file.size,
        mimeType: file.type,
        owner_id: this.subjectUserId,
      });
    },
  },
};
</script>

<style scoped>
/* Its own copy: the folder list styles this class in its own scoped block, and a scoped style
   does not reach another component — without it the box renders unaligned with the toolbar. */
.hide-empty-control {
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.hide-empty-control .custom-control-label {
  cursor: pointer;
}

/* Bootstrap's input-group lays its children in a row, so the button sits over the field's
   trailing edge rather than adding a segment to it. */
.folder-search {
  position: relative;
}

.folder-search .clear-search {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  z-index: 4;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 0;
  background: none;
  color: #9aa0a6;
  line-height: 1;
  padding: 0;
}

.folder-search .clear-search:hover {
  background: rgba(0, 0, 0, 0.06);
  color: var(--main-text-color, #222222);
}

.breadcrumb-link {
  text-decoration: none;
  vertical-align: baseline;
}

.breadcrumb-link:hover {
  text-decoration: underline;
}

/* Its own rule rather than `.mobile-tablet-only-force`, which hides from 1024px up while
   `.desktop-only` hides from 1024px down — at exactly 1024px neither would show. */
.trail-compact {
  display: none;
}

@media (max-width: 1024px) {
  .trail-compact {
    display: block;
  }
}

.trail-current {
  margin-top: 0.35rem;
}

@media (max-width: 1024px) {
  .folder-search {
    width: 100% !important;
    margin-bottom: 8px;
    /* Its `mr-3` is for sitting beside the buttons; stacked, it made the input a rem narrower
       than the button row beneath it. */
    margin-right: 0 !important;
  }

  /* The breadcrumb is the widest thing here, so everything below it takes the full width.
     Wrapping is confined to this breakpoint: on desktop the buttons belong beside the search. */
  .contents-actions {
    width: 100%;
    flex-wrap: wrap;
  }

  /* Its label cannot wrap, so sharing a row with the buttons left one of them stacked against
     it with no gap. */
  .hide-empty-control {
    width: 100%;
    margin-right: 0 !important;
    margin-bottom: 8px;
  }

  .contents-actions .btn {
    flex: 1 1 0;
  }

  .breadcrumb-link,
  .breadcrumb-link + span {
    word-break: break-word;
  }
}
</style>
